import {
  AppointmentMeta,
  AppointmentModel
} from "@devexpress/dx-react-scheduler";
import { useFlags } from "launchdarkly-react-client-sdk";
import * as React from "react";
import {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useLocation } from "react-router-dom";

import { datadogRum } from "@datadog/browser-rum";
import {
  addMinutes,
  differenceInMinutes,
  endOfWeek,
  format,
  isEqual,
  isWithinInterval,
  startOfWeek
} from "date-fns";
import { useDebounce, useErrors } from "hooks";
import { omit } from "lodash";
import { v4 as uuidV4 } from "uuid";

import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  selectSnackbarActionType,
  setActionType,
  setCustomMessage,
  setLabel,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import {
  setOpen as setConfirmChangeDialogOpen,
  setContent,
  setOnCancel,
  setOnChange,
  setOptions,
  setTitle
} from "components/confirm-dialog/confirm-dialog.slice";
import ErrorStateSnackbarContent from "components/error-state-snackbar-content";
import WarningSnackbarContent from "components/warning-snackbar-content/warning-snackbar-content.component";
import {
  APPOINTMENT_STATUS_CANCELED,
  APPOINTMENT_STATUS_COMPLETED,
  APPOINTMENT_STATUS_CONFIRMED,
  APPOINTMENT_STATUS_DRAFT,
  APPOINTMENT_STATUS_NO_SHOW,
  APPOINTMENT_STATUS_UNCONFIRMED,
  EVENT_TYPE_APPOINTMENT_BLOCK,
  EVENT_TYPE_APPOINTMENT_CODE,
  EVENT_TYPE_PERSONAL_EVENT,
  EVENT_TYPE_SCHEDULE_CODE,
  EVENT_TYPE_WORK_SHIFT_CODE,
  NON_FINAL_STATUSES_CODES,
  QUICK_FILTER_DEFAULT_TYPE,
  QUICK_FILTER_DRAFT_APPOINTMENTS,
  VISIBLE_STATUSES_CODES
} from "constants/appointments";
import {
  ALLOW_ENTERING_CANCELLATION_REASON,
  GLOBAL_PRACTICE_EVENTS_FEATURE,
  SCHEDULING_V2_FEATURE,
  SHOW_ALL_CLINICS,
  WEB_SCHEDULER_APPOINTMENTS_FILTER
} from "constants/feature-flags";
import {
  clearRecordMetric,
  selectCurrentClinic,
  selectCurrentPractice,
  selectCurrentRegion,
  selectCurrentUser,
  selectHasRegions,
  selectNumberOfPracticeClinics,
  selectRecordMetric,
  selectUserClinicsIds
} from "features/auth/auth-slice";
import { SelectAppointmentOutcomeProvider } from "hooks/use-appointment-outcome";
import { ScheduleNextAppointmentProvider } from "hooks/use-schedule-next-appointment";
import { UndeliveredDevicesProvider } from "hooks/use-undelivered-devices-check";
import {
  useAddAppointmentMutation,
  useDeleteAppointmentMutation,
  useUpdateAppointmentMutation
} from "services/appointments";
import { useGetClinicsQuery } from "services/clinics";
import { useGetEventsQuery } from "services/events";
import {
  useAddScheduleMutation,
  useDeleteScheduleMutation,
  useUpdateScheduleMutation
} from "services/schedules";
import {
  Appointment,
  AppointmentRecurrenceInput,
  DeleteAppointmentInput
} from "services/types/appointments.type";
import { Clinic } from "services/types/clinics.type";
import { AppointmentBlock, PersonalEvent } from "services/types/events.type";
import {
  DeleteScheduleInput,
  Schedule,
  ScheduleInstanceInput
} from "services/types/schedules.type";
import { MinimalStaffMember, StaffMember } from "services/types/staff.type";
import { isStaffMemberAvailableUtil } from "utils/build-staff-member-work-shift.util";
import {
  convertDateFromCurrentTimezoneToTimezone,
  isInThePast,
  isWithin24Hours,
  parseBackendDateOnly
} from "utils/common";
import {
  isAppointment,
  isAppointmentBlock,
  isAppointmentDeletable,
  isAppointmentEditable,
  isClinicEvent,
  isPersonalEvent,
  isPersonalWorkShiftEvent
} from "utils/events.util";
import { safeRefetch } from "utils/services.util";

import AppointmentForm from "../appointment-form";
import { AppointmentFormValues } from "../appointment-form/appointment-form.component";
import {
  closeForm,
  hidePreview,
  openForm,
  selectAppointmentTypesFilterValues,
  selectFilterValues,
  selectFiltersReady,
  selectIncludeSupervisingProvider,
  selectIsShowByClinicViewMode,
  selectStaffFilterValues,
  setEditRecurringEventType,
  setSelectedFromAvailableStaff,
  setShowFromAllLocations
} from "../scheduler-slice";
import {
  ALL_EVENTS,
  CURRENT_AND_FOLLOWING,
  CURRENT_EVENT
} from "../scheduler/confirmation-dialog/recurring-events-confirmation/edit-recurring-event-confirmation.util";
import EditRecurringEventDialogContent from "../scheduler/confirmation-dialog/recurring-events-confirmation/edit-recurring-event-dialog-content.component";
import { ReservedAppointmentTypesProvider } from "../scheduler/use-reserved-appointment-types.hook";
import getAppointmentsBaseFilter from "../utils/get-appointments-base-filters.util";
import { getEventsData } from "../utils/get-events-data.util";
import {
  calculateZIndex,
  findOverlappedAppointments,
  getClinicIdsFromEvents,
  sortAppointmentsByStartDate
} from "../utils/scheduling.utils";
import CloneEventDialogContent from "./components/clone-event-dialog-content.component";
import MoveEventDialogContent from "./components/move-event-dialog-content.component";
import useAppointmentCounters from "./use-appointments-counters.hook";
import useRoomValidation from "./use-appointments-room.hook";
import { getAppointmentPatientData } from "./utils/get-patient-data.util";

const AppointmentsContext = React.createContext<any>(null);

type AppointmentsProviderProps = {
  children?: React.ReactNode;
  selectedClinics?: Clinic[];
};

export type ActionType = "add" | "update" | "delete";
export const EVENTS_QUERY = "{*}";

export function AppointmentsProvider({
  children,
  selectedClinics
}: AppointmentsProviderProps) {
  const dispatch = useAppDispatch();
  const { isRoomAvailable } = useRoomValidation();
  const currentPractice = useAppSelector(selectCurrentPractice);
  const isShowByClinicView = useAppSelector(selectIsShowByClinicViewMode);
  const includeSupervisingProvider = useAppSelector(
    selectIncludeSupervisingProvider
  );
  const hasRegions = useAppSelector(selectHasRegions);
  const currentRegion = useAppSelector(selectCurrentRegion);
  const recordMetric = useAppSelector(selectRecordMetric);
  const numberOfPracticeClinics =
    useAppSelector(selectNumberOfPracticeClinics) || 0;
  const multiClinic = numberOfPracticeClinics > 1;
  const userClinicsIds = useAppSelector(selectUserClinicsIds);

  const [appointmentIdMutationInProgress, setAppointmentIdMutationInProgress] =
    useState<number | undefined>();

  const [editingForm, setEditingForm] = useState<boolean>(false);
  const appointmentMetaRef = useRef<AppointmentMeta | undefined>();
  const [currentAppointment, setCurrentAppointment] = useState<
    Partial<AppointmentFormValues>
  >({});
  const [originalAppointment, setOriginalAppointment] = useState<
    Partial<Appointment> | Partial<PersonalEvent> | Partial<AppointmentBlock>
  >({});
  const [onAppointmentSave, setOnAppointmentSave] = useState<any>();
  const [onAppointmentCancel, setOnAppointmentCancel] = useState<any>();
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [reference, setReference] = useState<any>();
  const flags = useFlags();
  const isGlobalPracticeEventEnabled = flags[GLOBAL_PRACTICE_EVENTS_FEATURE];
  const schedulingV2FeatureEnabled = flags[SCHEDULING_V2_FEATURE];
  const appointmentDraftsEnabled = flags[WEB_SCHEDULER_APPOINTMENTS_FILTER];
  const allowEnteringCancellationReason =
    flags[ALLOW_ENTERING_CANCELLATION_REASON];
  const showAllClinics = flags[SHOW_ALL_CLINICS];
  const appointmentsFilters = useAppSelector(selectFilterValues);
  const appointmentTypesValues = useAppSelector(
    selectAppointmentTypesFilterValues
  );

  const filtersReady = useAppSelector(selectFiltersReady);
  const currentClinic = useAppSelector(selectCurrentClinic);
  const currentUser = useAppSelector(selectCurrentUser);
  const selectedStaff = useAppSelector(selectStaffFilterValues);
  const actionType = useAppSelector(selectSnackbarActionType);

  const setAvailableStaff = useCallback(
    (value: MinimalStaffMember[]): any =>
      dispatch(setSelectedFromAvailableStaff(value as StaffMember[])),
    [dispatch]
  );
  const setShowAvailabilityFromAll = useCallback(
    (value: boolean) => dispatch(setShowFromAllLocations(value)),
    [dispatch]
  );
  const { pathname } = useLocation();

  const { data: allClinics } = useGetClinicsQuery(
    {
      query: "{id,name,timezone}",
      region: hasRegions ? currentRegion?.id : undefined,
      user:
        hasRegions || currentUser?.show_own_clinics_only
          ? currentUser?.id
          : undefined
    },
    {
      skip:
        !currentUser?.id ||
        currentUser?.id === -1 ||
        !currentClinic?.id ||
        (hasRegions && !currentRegion?.id)
    }
  );

  const isDaySheetPage = pathname === "/scheduling/day-sheet" || pathname === "/home";
  const isSchedulingPage =
    pathname === "/scheduling" ||
    pathname === "/scheduling/" ||
    pathname === "/scheduling/view" ||
    isDaySheetPage ||
    pathname === "/";

  const selectedClinicIds = useMemo(() => {
    if (currentUser?.show_own_clinics_only) {
      if (userClinicsIds.length > 0) return userClinicsIds;
      if (allClinics?.length) return allClinics.map((c) => c.id);
      return [currentClinic?.id].filter(Boolean);
    }
    return selectedClinics?.filter(Boolean)?.map((clinic) => clinic.id);
  }, [
    currentUser?.show_own_clinics_only,
    userClinicsIds,
    allClinics,
    currentClinic?.id,
    selectedClinics
  ]);

  const appointmentFormId = useMemo(() => {
    return uuidV4();
  }, []);

  const baseFilters = useMemo(() => {
    const baseFilter = isSchedulingPage
      ? getAppointmentsBaseFilter(appointmentsFilters.quickFilterCode, {
          status: VISIBLE_STATUSES_CODES
        })
      : {};

    return isSchedulingPage
      ? {
          ...appointmentsFilters,
          ...(isDaySheetPage && { clinicIds: undefined }),
          baseFilter: {
            ...baseFilter,
            ...(includeSupervisingProvider && {
              include_supervising_provider: true
            })
          },
          eventType: [
            EVENT_TYPE_APPOINTMENT_CODE,
            EVENT_TYPE_SCHEDULE_CODE
          ].join(","),
          query: EVENTS_QUERY
        }
      : {
          eventType: EVENT_TYPE_APPOINTMENT_CODE,
          quickFilterCode: QUICK_FILTER_DEFAULT_TYPE,
          fromDate: format(startOfWeek(new Date()), "yyyy-MM-dd"),
          toDate: format(endOfWeek(new Date()), "yyyy-MM-dd"),
          staffIds: [currentUser.id],
          clinicIds: selectedClinicIds,
          query: EVENTS_QUERY,
          baseFilter: {
            ...(includeSupervisingProvider && {
              include_supervising_provider: true
            })
          }
        };
  }, [
    isSchedulingPage,
    isDaySheetPage,
    appointmentsFilters,
    currentUser.id,
    selectedClinicIds,
    includeSupervisingProvider
  ]);

  const filters = useMemo(() => {
    return {
      ...baseFilters,
      staffIds: baseFilters.staffIds?.length
        ? baseFilters.staffIds
        : [currentUser.id]
    };
  }, [baseFilters, currentUser.id]);

  const { queriesCounters, isLoadingCounters } = useAppointmentCounters();

  const { value: dFilters, isDebouncing } = useDebounce(filters, 500);

  // ENG-1252: patient routes (/patients/:id, /patients/new) mount this provider
  // only to expose setupAppointmentModal + appointmentFormId; they never read
  // events/appointments/workShiftEvents from context. Skip the events fetches
  // there so the patient view isn't blocked by a week-window {*} events query.
  const isPatientRoute = pathname.startsWith("/patients/");

  const skipEventsFetching = useMemo(() => {
    return Boolean(
      isPatientRoute ||
        !Boolean(currentClinic?.id) ||
        (isSchedulingPage && !filtersReady) ||
        isLoadingCounters ||
        !baseFilters.staffIds?.length
    );
  }, [
    isPatientRoute,
    currentClinic?.id,
    isSchedulingPage,
    filtersReady,
    isLoadingCounters,
    baseFilters.staffIds?.length
  ]);

  const {
    data: events,
    refetch,
    isUninitialized,
    isLoading: isEventsLoading,
    isFetching: isEventsFetching
  } = useGetEventsQuery(dFilters, {
    skip: skipEventsFetching,
    // Pause polling while the appointment form is open. A mid-edit refetch
    // re-renders the DX scheduler tree, which can fire a spurious
    // onVisibilityChange and close the form — the trigger for SN-17825.
    pollingInterval: editingForm ? 0 : 600000,
    // Extend cache time for better performance
    refetchOnMountOrArgChange: 600
  });

  useEffect(() => {
    if (recordMetric) {
      if (isUninitialized || isEventsFetching) {
        if (!reference) {
          setReference(
            datadogRum?.startDurationVital?.("events-fetch", {
              description: "Region/Clinic Switch Events loading",
              context: { currentRegion, currentClinic, currentUser }
            })
          );
        }
      } else {
        if (reference) {
          datadogRum?.stopDurationVital(reference);
          setReference(undefined);
          dispatch(clearRecordMetric());
        }
      }
    }
  }, [
    isEventsFetching,
    recordMetric,
    isUninitialized,
    reference,
    setReference,
    currentRegion,
    currentClinic,
    currentUser,
    dispatch
  ]);

  const workShiftClinicIds: number[] = useMemo(() => {
    if (selectedClinicIds && selectedClinicIds.length > 0) {
      return selectedClinicIds;
    }
    return getClinicIdsFromEvents(
      events,
      allClinics,
      currentClinic?.id,
      isShowByClinicView,
      showAllClinics
    );
  }, [
    selectedClinicIds,
    currentClinic?.id,
    allClinics,
    showAllClinics,
    isShowByClinicView,
    events
  ]);

  const {
    data: workShiftEvents,
    refetch: refetchWorkShiftEvents,
    isUninitialized: isWorkShiftUninitialized,
    isLoading: isWorkShiftLoading,
    isFetching: isWorkShiftFetching
  } = useGetEventsQuery(
    {
      ...dFilters,
      ...(schedulingV2FeatureEnabled ? {} : { clinicIds: workShiftClinicIds }),
      eventType: EVENT_TYPE_WORK_SHIFT_CODE
    },
    {
      skip:
        skipEventsFetching ||
        isEventsFetching ||
        isEventsLoading ||
        isDebouncing,
      // More aggressive caching for work shifts (they change less frequently)
      refetchOnMountOrArgChange: 900 // 15 minutes
    }
  );

  const {
    appointments: rawAppointments,
    personalEvents,
    appointmentBlocks
  } = useMemo(
    () =>
      getEventsData(
        events,
        skipEventsFetching,
        selectedClinics,
        isGlobalPracticeEventEnabled && isShowByClinicView,
        currentClinic,
        selectedStaff,
        includeSupervisingProvider,
        showAllClinics ? (allClinics ?? []) : []
      ),
    [
      events,
      skipEventsFetching,
      selectedClinics,
      isShowByClinicView,
      isGlobalPracticeEventEnabled,
      currentClinic,
      selectedStaff,
      includeSupervisingProvider,
      allClinics,
      showAllClinics
    ]
  );

  // Filter out Web Scheduler DRAFT appointments from "All Appointments" view
  // but keep Google Calendar DRAFT appointments visible.
  // When user explicitly selects "Web Scheduler Appointments" filter, show all drafts.
  const appointments = useMemo(() => {
    if (!appointmentDraftsEnabled || !rawAppointments) {
      return rawAppointments;
    }

    // Don't filter when user explicitly selected "Web Scheduler Appointments" quick filter
    if (
      appointmentsFilters.quickFilterCode === QUICK_FILTER_DRAFT_APPOINTMENTS
    ) {
      return rawAppointments;
    }

    return rawAppointments.filter((apt: Appointment) => {
      // Keep all non-DRAFT appointments
      if (apt.status !== APPOINTMENT_STATUS_DRAFT) {
        return true;
      }

      // Keep DRAFT appointments from Google Calendar
      if (apt.googleCalendar) {
        return true;
      }

      // Filter out Web Scheduler DRAFT appointments
      return false;
    });
  }, [
    rawAppointments,
    appointmentDraftsEnabled,
    appointmentsFilters.quickFilterCode
  ]);

  // Count GC draft appointments shown in the scheduler but excluded from the backend counter
  // (backend excludes all drafts when appointmentDraftsEnabled, but GC drafts remain visible)
  const gcDraftCount = useMemo(() => {
    if (!appointmentDraftsEnabled) return 0;
    return appointments.filter(
      (apt: Appointment) =>
        apt.googleCalendar && apt.status === APPOINTMENT_STATUS_DRAFT
    ).length;
  }, [appointments, appointmentDraftsEnabled]);

  const augmentedCounters = useMemo(
    () => ({
      ...queriesCounters,
      [QUICK_FILTER_DEFAULT_TYPE]:
        queriesCounters[QUICK_FILTER_DEFAULT_TYPE] + gcDraftCount
    }),
    [queriesCounters, gcDraftCount]
  );

  const { workShift, personalWorkShift } = useMemo(
    () =>
      getEventsData(
        workShiftEvents,
        skipEventsFetching,
        selectedClinics,
        isGlobalPracticeEventEnabled && isShowByClinicView,
        currentClinic,
        selectedStaff,
        includeSupervisingProvider,
        showAllClinics ? (allClinics ?? []) : []
      ),
    [
      workShiftEvents,
      skipEventsFetching,
      selectedClinics,
      isShowByClinicView,
      isGlobalPracticeEventEnabled,
      currentClinic,
      selectedStaff,
      includeSupervisingProvider,
      allClinics,
      showAllClinics
    ]
  );

  const isStaffMemberAvailable = useCallback(
    ({
      staffMemberId,
      clinicId,
      startDate,
      endDate
    }: {
      startDate: string;
      endDate?: string;
      staffMemberId?: number;
      clinicId?: number;
    }) => {
      if (!startDate || !endDate || !staffMemberId) return false;

      const result = isStaffMemberAvailableUtil({
        staffMemberId,
        clinicId: !isShowByClinicView
          ? undefined
          : clinicId
            ? clinicId
            : currentClinic?.id,
        startDate: new Date(startDate),
        workShift
      });

      return result;
    },
    [isShowByClinicView, currentClinic?.id, workShift]
  );

  const [addScheduleMutation, { isLoading: isAddScheduleMutationLoading }] =
    useAddScheduleMutation();
  const [
    updateScheduleMutation,
    { isLoading: isUpdateScheduleMutationLoading }
  ] = useUpdateScheduleMutation();
  const [
    deleteScheduleMutation,
    { isLoading: isDeleteScheduleMutationLoading }
  ] = useDeleteScheduleMutation();

  const [addAppointment, { isLoading: isAddAppointmentMutationLoading }] =
    useAddAppointmentMutation();
  const [updateAppointment, { isLoading: isUpdateAppointmentLoading }] =
    useUpdateAppointmentMutation();
  const [deleteAppointment, { isLoading: isDeleteAppointmentLoading }] =
    useDeleteAppointmentMutation();

  const isLoading = useMemo(() => {
    return (
      !skipEventsFetching &&
      [
        isEventsFetching,
        isEventsLoading,
        isLoadingCounters,
        isWorkShiftUninitialized,
        isWorkShiftLoading,
        isWorkShiftFetching,
        isAddScheduleMutationLoading,
        isUpdateScheduleMutationLoading,
        isDeleteScheduleMutationLoading,
        isAddAppointmentMutationLoading,
        isUpdateAppointmentLoading,
        isDeleteAppointmentLoading,
        isDebouncing,
        !allClinics?.length
      ].some(Boolean)
    );
  }, [
    skipEventsFetching,
    isEventsFetching,
    isEventsLoading,
    isLoadingCounters,
    isWorkShiftUninitialized,
    isWorkShiftLoading,
    isWorkShiftFetching,
    isAddScheduleMutationLoading,
    isUpdateScheduleMutationLoading,
    isDeleteScheduleMutationLoading,
    isAddAppointmentMutationLoading,
    isUpdateAppointmentLoading,
    isDeleteAppointmentLoading,
    isDebouncing,
    allClinics?.length
  ]);
  const { errors, resetErrors, handleResponseErrors } = useErrors({
    fieldNames: ["start_date"],
    nonFieldNames: ["staff_member_unavailable", "room_unavailable"]
  });
  const confirmationMessages = useMemo(() => {
    const appointment = appointmentMetaRef.current?.data;
    if (appointment) {
      const couldBeDeleted = isAppointmentDeletable(appointment);
      return {
        discardButton: "Discard",
        confirmCancelMessage: "Discard unsaved changes?",
        deleteButton: couldBeDeleted ? "Delete" : "Yes",
        cancelButton: couldBeDeleted ? "Cancel" : "No",
        confirmDeleteMessage: `Are you sure you want to ${
          couldBeDeleted ? "delete" : "cancel"
        } this ${
          isPersonalEvent(appointment)
            ? "personal event"
            : isClinicEvent(appointment)
              ? "clinic event"
              : isAppointmentBlock(appointment)
                ? "dedicated appointment slot"
                : isPersonalWorkShiftEvent(appointment)
                  ? "work shift"
                  : "appointment"
        }?`
      };
    } else {
      return {
        discardButton: "Discard",
        deleteButton: "Delete",
        cancelButton: "Cancel",
        confirmDeleteMessage:
          "Are you sure you want to delete this appointment?",
        confirmCancelMessage: "Discard unsaved changes?"
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentMetaRef.current?.data]);

  const clearErrorNotifications = useCallback(() => {
    resetErrors();
    dispatch(setOpen(false));
    dispatch(setCustomMessage(undefined));
  }, [resetErrors, dispatch]);

  const handleAppointmentDeletion = useCallback(
    (appointment: AppointmentModel | PersonalEvent | AppointmentBlock) => {
      return (eventDeletionInput: DeleteAppointmentInput) => {
        if (isAppointmentDeletable(appointment)) {
          return deleteAppointment(eventDeletionInput);
        } else {
          const appointmentId =
            (appointment as Appointment & { modelId?: number }).modelId ??
            (appointment as Appointment).id;
          return updateAppointment({
            id: Number(appointmentId),
            status: APPOINTMENT_STATUS_CANCELED
          });
        }
      };
    },
    [deleteAppointment, updateAppointment]
  );

  // wrapped in useCallback to avoid re-rendering the component when the function is called
  const removeAppointment = useCallback(
    (eventId: number, updateType: string = "") => {
      const { data } = appointmentMetaRef.current || {};

      if (!data) {
        return;
      }

      let appointment: AppointmentModel | PersonalEvent | AppointmentBlock =
        data;

      const isCouldBeDeleted = isAppointmentDeletable(appointment);
      dispatch(
        setLabel(
          isPersonalEvent(appointment)
            ? "Personal Event"
            : isClinicEvent(appointment)
              ? "Clinic Event"
              : isAppointmentBlock(appointment)
                ? "Dedicated Appointment Slot"
                : isPersonalWorkShiftEvent(appointment)
                  ? "Work Shift"
                  : "Appointment"
        )
      );
      dispatch(setActionType(isCouldBeDeleted ? "delete" : "cancel"));
      setAppointmentIdMutationInProgress(Number(appointment?.id));

      const deleteAction = isAppointment(appointment)
        ? handleAppointmentDeletion(appointment)
        : deleteScheduleMutation;

      let deleteInput: any;
      if (!isAppointment(appointment)) {
        deleteInput = {
          scheduleId: Number.isInteger(eventId) ? eventId : appointment.modelId
        } as DeleteScheduleInput;
        if (
          updateType &&
          (appointment.recurrencePeriod ||
            appointment.rrule ||
            appointment.schedule?.rrule)
        ) {
          if ([CURRENT_EVENT, CURRENT_AND_FOLLOWING].includes(updateType)) {
            deleteInput["instance_start_date"] = format(
              new Date(appointment.startDate),
              "yyyy-MM-dd"
            );
            if (updateType === CURRENT_EVENT) {
              deleteInput["instance_end_date"] = format(
                new Date(appointment.endDate || appointment.startDate),
                "yyyy-MM-dd"
              );
            }
          }
        }
      } else {
        deleteInput = {
          appointmentId: eventId
        } as DeleteAppointmentInput;

        if (updateType && appointment.schedule) {
          if ([ALL_EVENTS, CURRENT_AND_FOLLOWING].includes(updateType)) {
            deleteInput["start_recurrence_id"] =
              updateType === CURRENT_AND_FOLLOWING
                ? appointment.id
                : appointment.schedule.appointment || undefined;
          }
        }
      }

      deleteAction(deleteInput as any).then(() => {
        if (!isUninitialized && !isEventsLoading) {
          safeRefetch(refetch, isUninitialized);
        }
        if (!isWorkShiftUninitialized && !isWorkShiftLoading) {
          safeRefetch(refetchWorkShiftEvents, isWorkShiftUninitialized);
        }
        dispatch(hidePreview());
        dispatch(setLabel("Appointment"));
        dispatch(setCustomMessage(undefined));
        dispatch(setOpen(1000));
        setAppointmentIdMutationInProgress(undefined);
      });
    },
    [
      deleteScheduleMutation,
      dispatch,
      handleAppointmentDeletion,
      isEventsLoading,
      isUninitialized,
      isWorkShiftLoading,
      isWorkShiftUninitialized,
      refetch,
      refetchWorkShiftEvents
    ]
  );

  // wrapped in useCallback to avoid re-rendering the component when the function is called
  const updateAppointmentStatus = useCallback(
    (
      initAppointmentOutcomeDialog: any,
      checkUndeliveredDevices?: (appointment: Appointment) => void,
      checkScheduleNextAppointment?: (appointment: Appointment) => void
    ) =>
      (appointment: Appointment, status: number) => {
        dispatch(
          setLabel(
            isPersonalEvent(appointment)
              ? "Personal Event"
              : isClinicEvent(appointment)
                ? "Clinic Event"
                : isAppointmentBlock(appointment)
                  ? "Dedicated Appointment Slot"
                  : isPersonalWorkShiftEvent(appointment)
                    ? "Work Shift"
                    : "Appointment"
          )
        );
        dispatch(
          setActionType(
            status === APPOINTMENT_STATUS_CANCELED ? "cancel" : "update"
          )
        );
        setAppointmentIdMutationInProgress(appointment.id);

        const collectOutcomeIsEnabled =
          status === APPOINTMENT_STATUS_COMPLETED &&
          (currentPractice?.collect_outcome ||
            appointment.appointmentType.is_opportunity);

        const collectCancellationReasonIsEnabled =
          [APPOINTMENT_STATUS_CANCELED, APPOINTMENT_STATUS_NO_SHOW].includes(
            status
          ) && allowEnteringCancellationReason;

        if (collectOutcomeIsEnabled || collectCancellationReasonIsEnabled) {
          const appointmentId =
            (appointment as Appointment & { modelId?: number }).modelId ??
            appointment.id;
          initAppointmentOutcomeDialog(
            Number(appointmentId),
            () => {
              if (!isUninitialized && !isEventsLoading) {
                safeRefetch(refetch, isUninitialized);
              }
              if (!isWorkShiftUninitialized && !isWorkShiftLoading) {
                safeRefetch(refetchWorkShiftEvents, isWorkShiftUninitialized);
              }
              dispatch(hidePreview());
              dispatch(setLabel("Appointment"));
              dispatch(setCustomMessage(undefined));
              dispatch(setOpen(1000));
              setAppointmentIdMutationInProgress(undefined);

              if (status === APPOINTMENT_STATUS_COMPLETED) {
                if (checkUndeliveredDevices) {
                  checkUndeliveredDevices(appointment);
                }
                if (checkScheduleNextAppointment) {
                  checkScheduleNextAppointment(appointment);
                }
              }
            },
            () => {
              setAppointmentIdMutationInProgress(undefined);
            },
            status
          );
        } else {
          const appointmentId =
            (appointment as Appointment & { modelId?: number }).modelId ??
            appointment.id;
          return updateAppointment({ id: Number(appointmentId), status }).then(
            () => {
              if (!isUninitialized && !isEventsLoading && !isEventsFetching) {
                safeRefetch(refetch, isUninitialized);
              }
              if (!isWorkShiftUninitialized && !isWorkShiftLoading) {
                safeRefetch(refetchWorkShiftEvents, isWorkShiftUninitialized);
              }
              dispatch(hidePreview());
              dispatch(setLabel("Appointment"));

              dispatch(setCustomMessage(undefined));
              dispatch(setOpen(1000));

              setAppointmentIdMutationInProgress(undefined);

              if (status === APPOINTMENT_STATUS_COMPLETED) {
                if (checkUndeliveredDevices) {
                  checkUndeliveredDevices(appointment);
                }
                if (checkScheduleNextAppointment) {
                  checkScheduleNextAppointment(appointment);
                }
              }
            }
          );
        }
      },
    [
      currentPractice?.collect_outcome,
      dispatch,
      isEventsFetching,
      isEventsLoading,
      isUninitialized,
      isWorkShiftLoading,
      isWorkShiftUninitialized,
      refetch,
      refetchWorkShiftEvents,
      updateAppointment,
      allowEnteringCancellationReason
    ]
  );

  // wrapped in useCallback to avoid re-rendering the component when the function is called
  const setupAppointmentModal = useCallback(
    (
      appointment: Appointment | PersonalEvent | AppointmentBlock,
      actionType: ActionType,
      onAppointmentSave: () => void,
      onAppointmentCancel: () => void,
      isRescheduling = false
    ) => {
      clearErrorNotifications();
      dispatch(setActionType(actionType));

      setOnAppointmentCancel(() => onAppointmentCancel);
      setOnAppointmentSave(() => onAppointmentSave);
      setIsRescheduling(isRescheduling);
      setOriginalAppointment(appointment);

      setAvailableStaff(
        appointment?.staffMember ? [appointment.staffMember] : []
      );

      const {
        patient,
        preferredClinic,
        preferredProvider,
        patientReferralSource,
        patientSubReferralSource
      } = getAppointmentPatientData(appointment);

      const clinicToOverride =
        allClinics?.find((clinic) => clinic.id === appointment?.clinicId) ??
        appointment.clinic;

      const clinic =
        appointment?.clinic ||
        preferredClinic ||
        (multiClinic ? null : currentClinic);
      setShowAvailabilityFromAll(!clinic);

      const formValues = {
        id: appointment?.id,
        event_type: appointment?.type,
        title: appointment?.title ?? "",
        notes: appointment?.notes,
        staff_member: appointment?.staffMember || preferredProvider,
        appointment_date: appointment?.startDate
          ? new Date(appointment.startDate)
          : new Date(),
        start_time: appointment?.startDate
          ? new Date(appointment.startDate)
          : new Date(),
        end_time: appointment?.endDate
          ? new Date(appointment.endDate)
          : addMinutes(new Date(), 60),
        clinic,
        timezone: clinic?.timezone,
        clinicIdToOverride: appointment?.clinicId,
        patient
      };

      if (clinicToOverride && clinicToOverride?.id !== appointment.clinic?.id) {
        formValues.clinic = clinicToOverride ?? formValues.clinic;
      }

      if (
        isPersonalEvent(appointment) ||
        isClinicEvent(appointment) ||
        isPersonalWorkShiftEvent(appointment)
      ) {
        setCurrentAppointment({
          ...formValues,
          modelId: appointment?.modelId || appointment?.id,
          is_global_practice_event: appointment?.isGlobalPracticeEvent,
          recurrence_period: appointment?.recurrencePeriod,
          recurrence_day: appointment?.recurrenceDay,
          recurrence_week: appointment?.recurrenceWeek,
          appointment_type: appointment?.appointment_type,
          is_recurring: Boolean(appointment?.recurrencePeriod),
          event_end_date: appointment?.endRecurrenceDate
            ? parseBackendDateOnly(appointment.endRecurrenceDate)
            : null,
          staff_members: appointment.staff_members ?? [],
          rrule: appointment.rrule
        } as Partial<AppointmentFormValues>);
      } else if (isAppointmentBlock(appointment)) {
        setCurrentAppointment({
          ...formValues,
          modelId: appointment?.modelId || appointment?.id,
          recurrence_period: appointment?.recurrencePeriod,
          recurrence_day: appointment?.recurrenceDay,
          recurrence_week: appointment?.recurrenceWeek,
          is_recurring: Boolean(appointment?.recurrencePeriod),
          event_end_date: appointment?.endRecurrenceDate
            ? parseBackendDateOnly(appointment.endRecurrenceDate)
            : null,
          appointment_type: appointment?.appointmentType,
          rrule: appointment.rrule
        } as Partial<AppointmentFormValues>);
      } else {
        // Calculate if the appointment currently overlaps with any blocks
        const appointmentStartDate = appointment?.startDate
          ? new Date(appointment.startDate).getTime()
          : undefined;
        const appointmentEndDate = appointment?.endDate
          ? new Date(appointment.endDate).getTime()
          : undefined;
        const staffMemberId = appointment?.staffMemberId;

        const currentIntersectingBlocks =
          appointmentStartDate && appointmentEndDate && appointmentBlocks
            ? appointmentBlocks.filter(
                (block: any) =>
                  block.staffMemberId === staffMemberId &&
                  ((isWithinInterval(new Date(appointmentStartDate), {
                    start: new Date(block.startDate),
                    end: new Date(block.endDate)
                  }) &&
                    !isEqual(
                      new Date(appointmentStartDate),
                      new Date(block.endDate)
                    )) ||
                    (isWithinInterval(new Date(appointmentEndDate), {
                      start: new Date(block.startDate),
                      end: new Date(block.endDate)
                    }) &&
                      !isEqual(
                        new Date(appointmentEndDate),
                        new Date(block.startDate)
                      )))
              )
            : [];

        const initiallyOverlapsWithBlock = currentIntersectingBlocks.some(
          (block: any) =>
            block.appointmentType?.id !== appointment?.appointmentType?.id
        );

        setCurrentAppointment({
          ...formValues,
          status: appointment?.status,
          room: appointment?.room,
          appointment_type: appointment?.appointmentType,
          referral_source:
            appointment?.referralSource &&
            typeof appointment.referralSource === "object"
              ? String(appointment.referralSource.id)
              : String(
                  appointment?.referralSource || patientReferralSource || ""
                ),
          sub_referral_source:
            appointment?.subReferralSource &&
            typeof appointment.subReferralSource === "object"
              ? appointment.subReferralSource.id
              : appointment?.subReferralSource ||
                patientSubReferralSource ||
                "",
          sub_referral_source_object:
            appointment?.subReferralSourceObject || null,
          referring_physician:
            appointment?.referringPhysician &&
            typeof appointment.referringPhysician === "object"
              ? appointment.referringPhysician
              : appointment?.referringPhysician || "",
          referral: appointment?.referral,
          companion_type: appointment?.companionType || "",
          companion_name: appointment?.companionName,
          patient: appointment?.patient,
          should_notify: appointment.shouldNotify,
          should_create_schedule_for_staff:
            appointment.shouldCreateScheduleForStaff,
          web_patient: appointment?.webPatient,
          notification_method: appointment?.notificationMethod,
          intake_form_request: appointment?.intake_form_request,
          should_create_intake_form_request:
            appointment?.should_create_intake_form_request,
          rescheduled_appointment_id: appointment?.rescheduled_appointment_id,
          schedule: appointment?.schedule,
          assigned_tags: appointment?.assignedTags,
          supervising_provider: appointment?.supervising_provider ?? null,
          is_direct_supervisor: appointment?.is_direct_supervisor ?? false,
          overwrite_block:
            actionType === "update" && initiallyOverlapsWithBlock
              ? true
              : undefined
        } as Partial<AppointmentFormValues>);
      }
      dispatch(setOpen(false));
    },
    [
      allClinics,
      appointmentBlocks,
      clearErrorNotifications,
      currentClinic,
      dispatch,
      multiClinic,
      setAvailableStaff,
      setShowAvailabilityFromAll
    ]
  );

  useEffect(() => {
    if (editingForm) {
      dispatch(openForm(appointmentFormId));
    } else {
      dispatch(closeForm());
    }
  }, [editingForm, dispatch, appointmentFormId]);

  const handleAppointmentActionResult = useCallback(
    (returnedAppointment: any) => {
      setAppointmentIdMutationInProgress(undefined);
      const responseErrors = handleResponseErrors(returnedAppointment);

      let isSuccess = false;
      if (responseErrors) {
        dispatch(
          setCustomMessage(
            <ErrorStateSnackbarContent errors={responseErrors} showAsWarning />
          )
        );
      } else if ("data" in returnedAppointment) {
        isSuccess = true;
        dispatch(closeForm());
        if (!isUninitialized && !isEventsLoading) {
          safeRefetch(refetch, isUninitialized);
        }
        if (!isWorkShiftUninitialized && !isWorkShiftLoading) {
          safeRefetch(refetchWorkShiftEvents, isWorkShiftUninitialized);
        }
        if (onAppointmentSave) {
          onAppointmentSave();
        }
      } else {
        dispatch(
          setCustomMessage(
            "An Error has Occurred, please reach out to support."
          )
        );
      }
      dispatch(setLabel("Appointment"));
      if (isSuccess) {
        dispatch(setCustomMessage(undefined));
      }
      dispatch(setOpen(1000));
      return { isSuccess, result: returnedAppointment };
    },
    [
      handleResponseErrors,
      onAppointmentSave,
      refetch,
      refetchWorkShiftEvents,
      dispatch,
      isUninitialized,
      isEventsLoading,
      isWorkShiftUninitialized,
      isWorkShiftLoading
    ]
  );

  const handleRecurringEventUpdate = useCallback(
    (
      appointmentAction: any,
      eventInput:
        | (Partial<Schedule> & ScheduleInstanceInput)
        | (Partial<Appointment> & AppointmentRecurrenceInput),
      actionLabel: string
    ) => {
      const message = `Are you sure you want to update this ${actionLabel}?`;
      dispatch(setTitle(message));

      const eventId = Number(eventInput?.id);
      setAppointmentIdMutationInProgress(eventId);

      dispatch(
        setOptions({
          actionButtonText: "Update"
        })
      );

      dispatch(
        setContent(
          <EditRecurringEventDialogContent
            eventType={actionLabel}
            eventEndTime={
              appointmentMetaRef.current?.data?.endDate
                ? String(appointmentMetaRef.current.data.endDate)
                : undefined
            }
            changeCallback={(updateType) => {
              const { data: appointment } = appointmentMetaRef.current || {};
              const instanceStartDate = format(
                new Date(appointment?.startDate ?? ""),
                "yyyy-MM-dd"
              );
              const instanceEndDate = format(
                new Date(appointment?.endDate ?? ""),
                "yyyy-MM-dd"
              );
              if (isAppointment(appointment) && appointment.schedule) {
                if ([ALL_EVENTS, CURRENT_AND_FOLLOWING].includes(updateType)) {
                  (
                    eventInput as Partial<Appointment> &
                      AppointmentRecurrenceInput
                  )["start_recurrence_id"] =
                    updateType === CURRENT_AND_FOLLOWING
                      ? appointment.id
                      : appointment.schedule.appointment || undefined;
                }
              } else {
                if (
                  [CURRENT_EVENT, CURRENT_AND_FOLLOWING].includes(updateType)
                ) {
                  (eventInput as Partial<Schedule> & ScheduleInstanceInput)[
                    "instance_start_date"
                  ] = instanceStartDate;
                  if (updateType === CURRENT_EVENT) {
                    (eventInput as Partial<Schedule> & ScheduleInstanceInput)[
                      "instance_end_date"
                    ] = instanceEndDate;
                  }
                } else {
                  if (instanceStartDate === (eventInput as any)["start_date"]) {
                    delete (eventInput as any)["start_date"];
                  }
                }
              }

              setOptions({
                actionButtonText: ""
              });
              //Send update recurring event request
              appointmentAction(eventInput).then(handleAppointmentActionResult);
            }}
          />
        )
      );

      dispatch(
        setOnCancel(() => {
          setAppointmentIdMutationInProgress(undefined);
          setOptions({
            actionButtonText: ""
          });
          dispatch(setEditRecurringEventType(""));
        })
      );
      dispatch(setConfirmChangeDialogOpen(true));
    },
    [dispatch, handleAppointmentActionResult]
  );

  const handleCreateAppointment = useCallback(
    (
      appointmentData: Partial<Appointment> | Partial<Schedule>,
      overrideActionType?: string
    ) => {
      clearErrorNotifications();
      let appointmentAction: any;
      let addAction: any = addAppointment;
      let updateAction: any = updateAppointment;
      let actionLabel = "Appointment";
      const eventInput = appointmentData;

      if (isPersonalEvent(eventInput)) {
        actionLabel = "Personal Event";
        addAction = addScheduleMutation;
        updateAction = updateScheduleMutation;
      } else if (isClinicEvent(eventInput)) {
        actionLabel = "Clinic Event";
        addAction = addScheduleMutation;
        updateAction = updateScheduleMutation;
      } else if (isPersonalWorkShiftEvent(eventInput)) {
        actionLabel = "Work Shift";
        addAction = addScheduleMutation;
        updateAction = updateScheduleMutation;
      } else if (isAppointmentBlock(eventInput)) {
        actionLabel = "Dedicated Appointment Slot";
        addAction = addScheduleMutation;
        updateAction = updateScheduleMutation;
      } else if (isAppointment(eventInput)) {
        if (eventInput.companionType) {
          eventInput.companion_present = true;
        }
        if (
          isAppointment(originalAppointment) &&
          originalAppointment.status === APPOINTMENT_STATUS_DRAFT &&
          Boolean(eventInput.patient)
        ) {
          eventInput.status =
            isWithin24Hours(eventInput.startDate) ||
            isInThePast(eventInput.startDate)
              ? APPOINTMENT_STATUS_CONFIRMED
              : APPOINTMENT_STATUS_UNCONFIRMED;
        }
      }
      dispatch(setLabel(actionLabel));
      if ((overrideActionType ?? actionType) === "add") {
        appointmentAction = addAction;
        if (overrideActionType) {
          dispatch(setActionType(overrideActionType as ActionType));
        }
      } else {
        appointmentAction = updateAction;
      }

      const isRRule = "rrule" in eventInput && eventInput.rrule;
      const isRecurrencePeriod =
        ("recurrence_period" in eventInput && eventInput.recurrence_period) ||
        currentAppointment.recurrence_period ||
        currentAppointment.schedule;

      if (
        (isRRule || isRecurrencePeriod) &&
        appointmentAction === updateAction
      ) {
        dispatch(closeForm());
        handleRecurringEventUpdate(
          appointmentAction,
          eventInput as Partial<Schedule>,
          actionLabel
        );
        return new Promise((resolve) => resolve({}));
      } else {
        return appointmentAction(eventInput).then(
          handleAppointmentActionResult
        );
      }
    },
    [
      clearErrorNotifications,
      addAppointment,
      updateAppointment,
      dispatch,
      actionType,
      currentAppointment.recurrence_period,
      addScheduleMutation,
      updateScheduleMutation,
      originalAppointment,
      handleRecurringEventUpdate,
      handleAppointmentActionResult,
      currentAppointment.schedule
    ]
  );

  const handleMovedEvent = useCallback(
    (changedData: {
      staffMemberId: number;
      clinicId: number;
      id: number;
      type: string;
      startDate: Date;
      endDate: Date;
      timezone: string;
      modelId?: number | null;
    }) => {
      clearErrorNotifications();

      const {
        id,
        type,
        startDate,
        endDate,
        staffMemberId,
        clinicId,
        timezone,
        modelId
      } = changedData;

      if (type === EVENT_TYPE_PERSONAL_EVENT || type === EVENT_TYPE_APPOINTMENT_BLOCK) {
        const scheduleEvent: Partial<Schedule> = {
          id: modelId || id,
          start_date: format(startDate, "yyyy-MM-dd"),
          end_date: format(endDate, "yyyy-MM-dd"),

          start_time: format(startDate, "HH:mm"),
          end_time: format(endDate, "HH:mm"),
          staff_member: staffMemberId,
          clinic: clinicId
        };

        return updateScheduleMutation(scheduleEvent).then(
          handleAppointmentActionResult
        );
      } else {
        const clinic = allClinics?.find((clinic) => clinic.id === clinicId);
        const appointment: Partial<Appointment> = {
          id: modelId || id,
          staffMemberId,
          clinic,
          startDate: format(
            convertDateFromCurrentTimezoneToTimezone(startDate, timezone),
            "yyyy-MM-dd HH:mm:ssXXX"
          ),
          endDate: format(
            convertDateFromCurrentTimezoneToTimezone(endDate, timezone),
            "yyyy-MM-dd HH:mm:ssXXX"
          )
        };

        return updateAppointment(appointment).then(
          handleAppointmentActionResult
        );
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleAppointmentActionResult, updateAppointment]
  );

  const handleCloneAppointment = useCallback(
    (appointment: Appointment | PersonalEvent, droppedMeta: any) => {
      const isPersonal = isPersonalEvent(appointment);
      const isClinic = isClinicEvent(appointment);
      const isWorkShift = isPersonalWorkShiftEvent(appointment);
      const isBlock = isAppointmentBlock(appointment);
      const eventTypeText = isPersonal ? "personal event" : isBlock ? "dedicated appointment slot" : "appointment";

      const newStaffMember = selectedStaff.find(
        (staff) => staff.id === droppedMeta.staffMemberId
      );

      const isDifferentStaffMember =
        appointment.staffMemberId !== newStaffMember?.id;

      const isDifferentClinic = appointment.clinicId !== droppedMeta?.clinicId;

      const newClinic = allClinics?.find(
        (clinic) => clinic.id === droppedMeta.clinicId
      );

      const confirmationText = `Are you sure you want to clone this ${eventTypeText}?`;

      dispatch(setTitle(confirmationText));
      dispatch(
        setContent(
          <CloneEventDialogContent
            confirmationText={confirmationText}
            startDate={droppedMeta.startDate}
            isDifferentStaffMember={isDifferentStaffMember}
            isDifferentClinic={isDifferentClinic}
            newStaffMemberFullName={newStaffMember?.fullName ?? ""}
            newClinicName={newClinic?.name ?? ""}
          />
        )
      );

      dispatch(
        setOnChange(() => {
          setAppointmentIdMutationInProgress(Number(appointment?.id));

          const duration = differenceInMinutes(
            new Date(String(appointment?.endDate)),
            new Date(String(appointment?.startDate))
          );
          const endDate = addMinutes(droppedMeta.startDate, duration);
          let eventDataToClone: Partial<Appointment> | Partial<Schedule> = {};

          if (isPersonal || isClinic || isWorkShift || isBlock) {
            eventDataToClone = {
              type: appointment.type,
              is_available: isWorkShift || isBlock,
              title: appointment.title,
              start_time: format(droppedMeta.startDate, "HH:mm:ss"),
              start_date: format(droppedMeta.startDate, "yyyy-MM-dd"),
              end_time: format(droppedMeta.endDate, "HH:mm:ss"),
              end_date: format(droppedMeta.endDate, "yyyy-MM-dd"),
              clinic: newClinic?.id ?? appointment.clinic.id,
              staff_member:
                isPersonal || isWorkShift || isBlock ? droppedMeta.staffMemberId : null,
              ...(isBlock && (appointment as any).appointmentType
                ? { appointment_types: [(appointment as any).appointmentType.id] }
                : {})
            };
          } else {
            const clinic = newClinic ?? appointment.clinic;
            eventDataToClone = {
              ...omit(appointment, ["id", "status"]),
              clinic,
              staffMemberId: newStaffMember?.id ?? appointment.staffMember.id,
              startDate: format(
                convertDateFromCurrentTimezoneToTimezone(
                  droppedMeta.startDate,
                  clinic?.timezone
                ),
                "yyyy-MM-dd HH:mm:ssXXX"
              ),
              endDate: format(
                convertDateFromCurrentTimezoneToTimezone(
                  endDate,
                  clinic?.timezone
                ),
                "yyyy-MM-dd HH:mm:ssXXX"
              )
            };
          }

          handleCreateAppointment(eventDataToClone, "add");
        })
      );
      dispatch(
        setOnCancel(() => {
          setAppointmentIdMutationInProgress(undefined);
        })
      );
      startTransition(() => {
        dispatch(setConfirmChangeDialogOpen(true));
      });
    },
    [selectedStaff, allClinics, dispatch, handleCreateAppointment]
  );

  const handleMoveEvent = useCallback(
    async (event: Appointment | PersonalEvent, droppedMeta: any) => {
      const newStaffMember = selectedStaff.find(
        (staff) => staff.id === droppedMeta.staffMemberId
      );

      const isDifferentStaffMember = event.staffMemberId !== newStaffMember?.id;

      const isDifferentClinic = event.clinicId !== droppedMeta?.clinicId;

      const prevClinicName =
        allClinics?.find((clinic) => clinic.id === event.clinicId)?.name ?? "";

      const newClinicName =
        allClinics?.find((clinic) => clinic.id === droppedMeta.clinicId)
          ?.name ?? "";

      const previousStartDate = format(
        new Date(String(event.startDate)),
        "EEE MMM dd, yyyy hh:mm aaa"
      );

      const previousEndDate = format(
        new Date(String(event.endDate)),
        "hh:mm aaa"
      );

      const newStartDate = format(
        droppedMeta.startDate,
        "EEE MMM dd, yyyy hh:mm aaa"
      );

      const newEndDate = format(droppedMeta.endDate, "hh:mm aaa");

      const isSameDate = previousStartDate === newStartDate;

      let hasRoomConflict = false;
      let roomName = undefined;
      if (isAppointment(event) && event.room?.id) {
        roomName = event.room?.name;
        const isRoomAvailableResult = await isRoomAvailable(
          event.room,
          new Date(droppedMeta.startDate),
          new Date(droppedMeta.startDate),
          new Date(droppedMeta.endDate),
          event.title,
          event.patient,
          event.clinic,
          event.id
        );
        hasRoomConflict = !isRoomAvailableResult;
      }
      dispatch(setTitle(
        isAppointmentBlock(event)
          ? "Are you sure you want to move this dedicated appointment slot?"
          : "Are you sure you want to move this appointment?"
      ));
      dispatch(
        setContent(
          <MoveEventDialogContent
            isSameDate={isSameDate}
            isDifferentClinic={isDifferentClinic}
            isDifferentStaffMember={isDifferentStaffMember}
            prevStartDate={previousStartDate}
            prevEndDate={previousEndDate}
            prevStaffMemberFullName={event?.staffMember?.fullName}
            newStaffMemberFullName={newStaffMember?.fullName ?? ""}
            prevClinicName={prevClinicName}
            newClinicName={newClinicName}
            newStartDate={newStartDate}
            newEndDate={newEndDate}
            hasRoomConflict={hasRoomConflict}
            roomName={roomName}
          />
        )
      );

      const eventId = Number("modelId" in event ? event.modelId : event?.id);
      dispatch(
        setOnChange(() => {
          setAppointmentIdMutationInProgress(eventId);
          handleMovedEvent({
            ...droppedMeta,
            type: event.type,
            id: eventId,
            timezone: event.clinic.timezone
          });
        })
      );
      dispatch(
        setOnCancel(() => {
          setAppointmentIdMutationInProgress(undefined);
        })
      );
      dispatch(setConfirmChangeDialogOpen(true));
    },
    [handleMovedEvent, dispatch, selectedStaff, allClinics, isRoomAvailable]
  );

  const handleDroppedPersonalEvent = useCallback(
    (event: PersonalEvent, droppedMeta: any) => {
      const startDate = new Date(String(event?.startDate));

      const isDifferentClinic = event.clinicId !== droppedMeta.clinicId;

      if (!isDifferentClinic && isEqual(droppedMeta.startDate, startDate)) {
        return;
      }

      if (
        (!isAppointmentEditable(event) || startDate < new Date()) &&
        droppedMeta.startDate >= new Date()
      ) {
        handleCloneAppointment(event, droppedMeta);
      } else if (droppedMeta.startDate < new Date()) {
        dispatch(
          setCustomMessage(
            <WarningSnackbarContent title="Moving personal events to the past is currently restricted" />
          )
        );
        dispatch(setLabel("Appointment"));
        dispatch(setOpen(1000));
      } else {
        handleMoveEvent(event, droppedMeta);
      }
    },
    [handleCloneAppointment, handleMoveEvent, dispatch]
  );

  const handleDroppedAppointment = useCallback(
    (appointment: Appointment, droppedMeta: any) => {
      const startDate = new Date(String(appointment?.startDate));
      const endDate = new Date(String(appointment?.endDate));
      const appointmentFiniteStatus = !NON_FINAL_STATUSES_CODES.includes(
        appointment?.status
      );

      const isDifferentClinic = appointment.clinicId !== droppedMeta.clinicId;

      const isDifferentStaffMember =
        droppedMeta.staffMemberId !== appointment?.staffMemberId;

      if (
        !differenceInMinutes(droppedMeta.startDate, startDate) &&
        (isDifferentClinic || isDifferentStaffMember) &&
        droppedMeta.startDate >= new Date()
      ) {
        handleMoveEvent(appointment, droppedMeta);
        return;
      }

      if (
        !differenceInMinutes(droppedMeta.startDate, startDate) &&
        !differenceInMinutes(droppedMeta.endDate, endDate) &&
        !isDifferentClinic
      ) {
        return;
      }
      if (!isAppointmentEditable(appointment) && appointmentFiniteStatus) {
        handleCloneAppointment(appointment, droppedMeta);
      } else if (
        !differenceInMinutes(droppedMeta.startDate, startDate) &&
        differenceInMinutes(droppedMeta.endDate, endDate)
      ) {
        handleMoveEvent(appointment, droppedMeta);
      } else if (
        appointmentFiniteStatus &&
        droppedMeta.startDate < new Date()
      ) {
        dispatch(
          setCustomMessage(
            <WarningSnackbarContent title="Cloning appointments to the past is currently restricted" />
          )
        );
        dispatch(setLabel("Appointment"));
        dispatch(setOpen(1000));
      } else {
        handleMoveEvent(appointment, droppedMeta);
      }
    },
    [handleCloneAppointment, handleMoveEvent, dispatch]
  );

  const handleDroppedEvent = useCallback(
    (eventBeforeChange: Appointment | PersonalEvent, droppedMeta: any) => {
      /*
      droppedMeta doesn't contain type property, at the same time
       appointments and personalEvents could have similar id values,
       so we need pass eventBeforeChange object here
       */

      if (isAppointment(eventBeforeChange)) {
        handleDroppedAppointment(eventBeforeChange, droppedMeta);
      } else if (isAppointmentBlock(eventBeforeChange)) {
        handleDroppedPersonalEvent(eventBeforeChange, droppedMeta);
      } else if (isPersonalEvent(eventBeforeChange)) {
        handleDroppedPersonalEvent(eventBeforeChange, droppedMeta);
      } else if (isClinicEvent(eventBeforeChange)) {
        handleDroppedPersonalEvent(eventBeforeChange, droppedMeta);
      } else if (isPersonalWorkShiftEvent(eventBeforeChange)) {
        handleDroppedPersonalEvent(eventBeforeChange, droppedMeta);
      }
    },
    [handleDroppedAppointment, handleDroppedPersonalEvent]
  );

  const hasIntersectingEvents = useCallback(
    (
      startDate: Date | string,
      endDate: Date | string,
      staffMemberId: number | undefined
    ) => {
      if (
        !staffMemberId ||
        !startDate ||
        !endDate ||
        new Date(endDate) < new Date(startDate)
      )
        return false;

      let isOverlaps = Boolean(
        appointments.find(
          (appointment) =>
            appointment.staffMemberId === staffMemberId &&
            isWithinInterval(new Date(startDate), {
              start: new Date(appointment?.startDate),
              end: new Date(appointment?.endDate)
            }) &&
            !isEqual(new Date(appointment?.endDate), new Date(startDate))
        )
      );

      if (!isOverlaps) {
        isOverlaps = Boolean(
          personalEvents.find(
            (personalEvent) =>
              personalEvent.staffMemberId === staffMemberId &&
              isWithinInterval(new Date(startDate), {
                start: new Date(personalEvent?.startDate),
                end: new Date(personalEvent?.endDate)
              }) &&
              !isEqual(new Date(personalEvent?.endDate), new Date(startDate))
          )
        );
      }

      return isOverlaps;
    },
    [appointments, personalEvents]
  );

  const lastDraggedAppointment = React.useRef<any>();

  const updateAppointmentMetaData = useCallback(
    (appointmentId: number, updatedData: Partial<any>) => {
      if (appointmentMetaRef.current?.data?.id === appointmentId) {
        appointmentMetaRef.current = {
          ...appointmentMetaRef.current,
          data: {
            ...appointmentMetaRef.current.data,
            ...updatedData
          }
        };
      }
    },
    []
  );

  const appointmentTypesSet = useMemo(() => {
    return new Set(appointmentTypesValues);
  }, [appointmentTypesValues]);

  const filteredAppointments = useMemo(() => {
    if (appointmentTypesSet.size === 0) {
      return [...appointmentBlocks, ...appointments, ...personalEvents];
    }

    const filteredBlocks = appointmentBlocks.filter(
      ({ appointmentType }) =>
        appointmentType?.id && appointmentTypesSet.has(appointmentType.id)
    );

    const filteredAppointments = appointments.filter(({ appointmentType }) =>
      appointmentTypesSet.has(appointmentType?.id)
    );

    return [...filteredBlocks, ...filteredAppointments, ...personalEvents];
  }, [appointmentBlocks, appointmentTypesSet, appointments, personalEvents]);

  const positionedAppointments = useMemo(() => {
    if (filteredAppointments.length === 0) {
      return [];
    }

    const sortedAppointments =
      sortAppointmentsByStartDate(filteredAppointments);
    const zIndexMap = new Map<number | string, number>();

    return sortedAppointments.map((appointment, index) => {
      const overlappedAppointments = findOverlappedAppointments(
        sortedAppointments,
        index,
        appointment
      );
      const overlappedAppointment = overlappedAppointments.reduce(
        (maxZIndexAppointment, currentAppointment) => {
          const currentZIndex = zIndexMap.get(currentAppointment.id) || 0;
          const maxZIndex = zIndexMap.get(maxZIndexAppointment.id) || 0;
          return currentZIndex > maxZIndex
            ? currentAppointment
            : maxZIndexAppointment;
        },
        appointment
      );
      const zIndex = calculateZIndex(
        overlappedAppointment,
        appointment,
        zIndexMap
      );
      zIndexMap.set(appointment.id, zIndex);
      return {
        ...appointment,
        zIndex
      };
    });
  }, [filteredAppointments]);

  const safeRefetchEvents = useCallback(
    () => safeRefetch(refetch, isUninitialized),
    [refetch, isUninitialized]
  );

  const contextValue = useMemo(
    () => ({
      appointments: positionedAppointments,
      setupAppointmentModal,
      removeAppointment,
      updateAppointmentStatus,
      queriesCounters: augmentedCounters,
      appointmentMetaRef,
      confirmationMessages,
      editingForm,
      setEditingForm,
      saveChangedAppointment: handleMovedEvent,
      handleDroppedEvent,
      appointmentIdMutationInProgress,
      setAppointmentIdMutationInProgress,
      isLoading,
      resetErrors,
      hasIntersectingEvents,
      lastDraggedAppointment,
      refetchEvents: safeRefetchEvents,
      isStaffMemberAvailable,
      personalWorkShift,
      appointmentFormId,
      updateAppointmentMetaData
    }),
    [
      appointmentFormId,
      appointmentIdMutationInProgress,
      confirmationMessages,
      editingForm,
      handleDroppedEvent,
      handleMovedEvent,
      hasIntersectingEvents,
      isLoading,
      isStaffMemberAvailable,
      personalWorkShift,
      positionedAppointments,
      augmentedCounters,
      safeRefetchEvents,
      removeAppointment,
      resetErrors,
      setupAppointmentModal,
      updateAppointmentStatus,
      updateAppointmentMetaData
    ]
  );

  const handleOnCancel = useCallback(() => {
    if (onAppointmentCancel) {
      onAppointmentCancel();
    }
  }, [onAppointmentCancel]);

  return (
    <AppointmentsContext.Provider value={contextValue}>
      <SelectAppointmentOutcomeProvider>
        <ScheduleNextAppointmentProvider
          setupAppointmentModal={setupAppointmentModal}
          appointmentFormId={appointmentFormId}
        >
          <UndeliveredDevicesProvider>
            <ReservedAppointmentTypesProvider>
              {children}
              <AppointmentForm
                formId={appointmentFormId}
                onCancel={handleOnCancel}
                onSave={handleCreateAppointment}
                currentAppointment={currentAppointment}
                errors={errors}
                isRescheduling={isRescheduling}
              />
            </ReservedAppointmentTypesProvider>
          </UndeliveredDevicesProvider>
        </ScheduleNextAppointmentProvider>
      </SelectAppointmentOutcomeProvider>
    </AppointmentsContext.Provider>
  );
}

export default function useAppointments() {
  const appointmentsContext = React.useContext(AppointmentsContext);
  if (!appointmentsContext) {
    throw new Error(
      `useAppointments must be used within a AppointmentsProvider`
    );
  }
  return appointmentsContext;
}
